import { CollectedItemDetails } from "@apiserver-rest/ponder-data-client-base";
import { FilteredPreviewItem } from "./filtered-preview-item";
import { getFromLocalStorage, getFromSessionStorage } from "@ponder-shared/utils/localStorage";

export enum FilterType {
    Select = 0,
    Text = 1,
    Sort = 3,
    MagicColorIdentity = 4,
    MagicManaProduction = 5
}
/**
 * Base class of a specific filter such as "filter by color" or "filter by artist".
 */
export abstract class PreviewItemFilterOption {
    protected isCollection: boolean = false;
    protected isAuthed: boolean = false;
    public wasUpdated: boolean = true;

    public constructor(
        public label: string, 
        public filterType: FilterType, 
        public placeholder: string,
        private defaultValue: string,
        private defaultCollectionValue: string) { 
            
        }

    // -------------------------------------------------------------------------------------
    // Abstract Methods
    // -------------------------------------------------------------------------------------

    /**
     * Prepares the individual filter to receive a new batch of items.
     */
    public abstract prepare(): void;

    /**
     * Records the item in the filter. Used for filters which display a count of matching items.
     * @param data
     * @param collectedInfo 
     */
    public abstract track(data: any, collectedInfo: CollectedItemDetails | undefined): void;

    /**
     * Invoked when a new filter option has been selected.
     * For example, when filtering by color this gets called when "Green" is chosen.
     * 
     * @param selection
     * @param page 
     */
    public abstract updateSelection(selection: string, page: string): boolean;

    /**
     * Returns the sorted individual filters along with the number of matching items if available.
     */
    public abstract getFilters(): Map<string, number | undefined>;

    /**
     * Returns true if the specified item is "valid" according to the current filter settings.
     * @param data 
     * @param collectedInfo 
     */
    public abstract isValid(data: any, collectedInfo: CollectedItemDetails | undefined): boolean;

    /**
     * If this is a sorting "filter", sorts the provided item array.
     * @param items
     */
    public abstract sort(items: FilteredPreviewItem[]): void;

    // -------------------------------------------------------------------------------------
    // Misc
    // -------------------------------------------------------------------------------------
    
    public setUserInfo(isCollection: boolean, isAuthed: boolean): void {
        this.isCollection = isCollection;
    }

    public loadStoredFilter(page: string): string | undefined {
        var stored = this.getStoredFilter(page);

        if (stored && this.updateSelection(stored, page)) {
            return stored;
        } else {
            return undefined;
        }
    }

    protected setStoredFilter(value: string, page: string): void {
        var key = this.getStorageKey(page);

        if (this.filterType == FilterType.Sort) {
            localStorage.setItem(key, value);       // Store sorting longer term than an arbitrary filter
        } else {
            sessionStorage.setItem(key, value);
        }
    }

    protected getStoredFilter(page: string): string | undefined {
        var key = this.getStorageKey(page);
        var storedFilter = (this.filterType == FilterType.Sort ? 
            getFromLocalStorage(key) : 
            getFromSessionStorage(key));

        if (!storedFilter) {
            storedFilter = (this.isCollection ? this.defaultCollectionValue : this.defaultValue);
        }

        return storedFilter;
    }

    private getStorageKey(page: string): string {
        var prefix = `preview-item-filter:${this.isCollection ? "c:" : ""}`;

        if (this.filterType == FilterType.Sort) {
            return `${prefix}${this.label}`;
        } else {
            return `${prefix}${page}:${this.label}`;
        }
    }
}